import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { parsePhoneNumberFromString } from 'libphonenumber-js'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('America/New_York')

export const formatDate = (value) => {
  if (!value) return ''
  const date = dayjs(value)
  if (!date.isValid()) return value
  return date.format('MM/DD/YYYY')
}

export const formatLongDate = (value) => {
  if (!value) return ''
  const date = dayjs(value)
  if (!date.isValid()) return value
  return date.format('MMMM D, YYYY')
}

export const maskDob = (value) => {
  const formatted = formatDate(value)
  return formatted ? `XX/XX/${formatted.slice(-4)}` : ''
}

export const maskValue = (val) => (val ? 'XXXX' : '')

export const splitDateTime = (datetime) => {
  if (!datetime) {
    return {
      date: null,
      time: null,
    }
  }
  return {
    date: dayjs(datetime).format('MM/DD/YYYY'),
    time: dayjs(datetime).format('HH:mm:ss'),
  }
}

export const upperCaseFirstLetter = (word) => {
  return word.charAt(0) + word.substring(1).toLowerCase()
}

export const lowerCaseAllWordsExceptFirstLetters = (string) => {
  return string?.replaceAll(/\S*/g, (word) => `${word.slice(0, 1)}${word.slice(1).toLowerCase()}`)
}

export const getFormattedDate = (datetime) => {
  return splitDateTime(datetime)?.date
}

export const getFormattedTime = (datetime) => {
  return splitDateTime(datetime)?.time
}

export function formatCurrency(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return ''
  return `$${Number.parseFloat(value).toFixed(2)}`
}

export function shortenYearFormatter(dateString) {
  const month = dateString.slice(0, 2)
  const day = dateString.slice(2, 4)
  const year = dateString.slice(-2) // Extract last two digits for YY
  return `${month}${day}${year}`
}

export const getYesterdayDate = () => {
  const yesterday = dayjs().subtract(1, 'day')
  return yesterday.format('MMDDYYYY')
}

export const getTodayDate = () => {
  const today = dayjs()
  return today.format('YYYY-MM-DD')
}

export function prefixWithZero(input, desiredLength) {
  if (input === null || input === undefined) return ''

  if (typeof input === 'number') {
    return input.toString().padStart(desiredLength, '0')
  } else {
    return input.padStart(desiredLength, '0')
  }
}

const formatErrorItem = (item) => {
  if (!item) {
    return item
  }

  item.totalAmount = formatCurrency(item?.totalAmount)

  if (item?.driverService) {
    const dateTime = splitDateTime(item.driverService?.transaction?.completedTimestamp)
    item.driverService.transaction.completedDate = dateTime.date
    item.driverService.transaction.completedTime = dateTime.time

    const dateTestTime = splitDateTime(item?.testTimestamp)
    item.testDate = dateTestTime.date
    item.testTime = dateTestTime.time
  }

  // Format transaction details
  if (item?.transaction) {
    item.transaction.driverSubTotalAmount = formatCurrency(item.transaction?.driverSubTotalAmount)
    item.transaction.vehicleSubTotalAmount = formatCurrency(item.transaction?.vehicleSubTotalAmount)

    const dateTime = splitDateTime(item.transaction?.completedTimestamp)
    item.completedDate = dateTime.date
    item.completedTime = dateTime.time
  }

  if (item?.duplicates !== undefined) {
    item.duplicates = prefixWithZero(item.duplicates, 2)
  }

  return item
}

//Helper to process API response (array or single object)
export const processData = (data) => {
  if (Array.isArray(data)) {
    return data.map(formatErrorItem)
  }
  return formatErrorItem(data)
}

export const formatHeight = (heightInInches) => {
  if (!heightInInches) return ''
  const feet = Math.floor(heightInInches / 12)
  const inches = heightInInches % 12
  return `${feet}' ${inches}"`
}

export const formatDateForApi = (value) => {
  if (!value) return ''

  if (typeof value === 'string') {
    return value.split('T')[0]
  }

  if (value instanceof Date) {
    return value.toISOString().split('T')[0]
  }

  const date = dayjs(value)
  return date.isValid() ? date.format('MM/DD/YYYY') : ''
}

export const formatNewLineChars = (input) => input.replaceAll(String.raw`\n`, '\n')
export const formatUSPhone = (phone) => {
  if (!phone) {
    return phone
  }
  // Format as NNN-NNN-NNNN or default to the raw input if invalid
  const number = parsePhoneNumberFromString(phone, 'US')
  return number ? number.nationalNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') : phone
}

export const getUnformattedPhone = (phone) => {
  if (phone) {
    const number = parsePhoneNumberFromString(phone, 'US')
    // Returns 9991234567 or the raw string if invalid
    return number ? number.nationalNumber : phone
  }
  return null
}

export const formatDateTime = (dateTimeString) => {
  return dayjs(dateTimeString).format('MM/DD/YYYY HH:mm:ss')
}

export const stripNonDigits = (value) =>
  String(value ?? '')
    .split('')
    .filter((ch) => /\d/.test(ch))
    .join('')

export const isValidDate = (mm, dd, yyyy) => {
  const date = new Date(`${yyyy}-${mm}-${dd}`)
  return (
    !Number.isNaN(date.getTime()) &&
    date.getUTCFullYear() === +yyyy &&
    date.getUTCMonth() + 1 === +mm &&
    date.getUTCDate() === +dd
  )
}

export const formatDateInput = (digits) => {
  if (digits.length === 0) return ''
  let formatted = digits.slice(0, 2)
  if (digits.length > 2) formatted += '/' + digits.slice(2, 4)
  if (digits.length > 4) formatted += '/' + digits.slice(4, 8)
  return formatted
}

export const expandShortYear = (yyyy, optimizeForAge = false) => {
  if (yyyy.length === 2 && yyyy !== '19' && yyyy !== '20') {
    const currentYearShort = new Date().getFullYear() % 100
    let century
    if (optimizeForAge) {
      // If the year entered is less than (CurrentYear - 16). It's plausible they are a new 16+ year old driver in the 2000s.
      // Otherwise, we assume they are a senior born in the 1900s.
      century = +yyyy <= currentYearShort - 16 ? '20' : '19'
    } else {
      // For non-age fields (e.g. expiration dates), assume the current century.
      century = '20'
    }
    return century + yyyy
  }
  return yyyy
}

export const getTodayDisplayDateTime = () => {
  const today = dayjs()
  return { date: today.format('MM/DD/YYYY'), time: `${today.format('hh:mm A')} ET` }
}

export const formatAddress = (addressline1, addressline2, city, state, zip) => {
  const streetParts = []

  if (addressline1) streetParts.push(addressline1)
  if (addressline2) streetParts.push(addressline2)

  const street = streetParts.join(', ')

  // Format: city, state zip
  const cityLine = `${city ? city + ', ' : ''}${state || ''}${zip ? ' ' + zip : ''}`

  return {
    street,
    cityLine,
  }
}
